// admin/pages/PageEditor.tsx

import { useState, useEffect } from 'react';
import { pageService } from '../../../services/pageService';
import useCustomParams from '../../../hooks/core/useCustomParams';
import useToken from '../../../hooks/features/auth/useToken';
import usePages from '../../../hooks/features/pages/usePages';
import { useProduct } from '../../../hooks/core/useProduct';
import { Page } from '../../../types';
import { FieldConfig } from '../../../types/core/JsonParse';
import { DynamicForm } from '../../../components/ui/DynamicJSONForm';
import { parseContentToFields } from '../../../utils/parseContentToFields';
import HorizontalTab, { TabItem } from '../../../components/ui/HorizontalTab';
import useLocales from '../../../hooks/features/locales/useLocales';

export default function PageEditorPage() {
    const { id: pageId } = useCustomParams();
    const { token } = useToken();
    const [page, setPage] = useState<Page | null>(null);
    const { selectedProduct } = useProduct();
    const { locales } = useLocales();

    const [selectedLocale, setSelectedLocale] = useState<string>('fr');
    const [content, setContent] = useState<Array<{ key: string; label: string; value: any }>>([]);
    const [fields, setFields] = useState<FieldConfig[]>([]);
    const { fetchPageById, updatePage } = usePages(selectedProduct || "izi-call");

    useEffect(() => {
        loadPage();
    }, [pageId]);

    const loadPage = async () => {
        const data = await fetchPageById(pageId);

        if (data) {
            setPage(data);

            // 🔥 Récupérer le contenu pour la langue sélectionnée
            const contentObj = data.content || {};
            const contentForLocale = contentObj[selectedLocale] || [];
            console.log("content" , data.content)

            setContent(contentForLocale);
            setFields(parseContentToFields(contentForLocale));
        }
    };

    // 🔥 Changer de langue
    const onLocaleChange = (locale: string) => {
        setSelectedLocale(locale);

        // Mettre à jour le contenu affiché
        if (page) {
            const contentObj = page.content || {};
            const contentForLocale = contentObj[locale] || [];
            setContent(contentForLocale);
            setFields(parseContentToFields(contentForLocale));
        }
    };

    // 🔥 Modifier une valeur dans le tableau
    const handleChange = (key: string, value: any) => {
        const newContent = content.map(item =>
            item.key === key ? { ...item, value } : item
        );
        setContent(newContent);
        setFields(parseContentToFields(newContent));
    };

    const handleSave = async () => {
        // 🔥 Récupérer le contenu actuel de la page
        const currentContent = page?.content || {};

        // 🔥 Mettre à jour le contenu pour la langue sélectionnée
        const updatedContent = {
            ...currentContent,
            [selectedLocale]: content
        };

        await pageService.updateContent(token, pageId, updatedContent);
        alert('Page mise à jour');
    };

    // 🔥 Construire les onglets
    const localesTab: TabItem[] = locales
        .filter(l => l.is_active)
        .map((l) => ({
            label: l.flag ? `${l.flag} ${l.name}` : l.name,
            value: l.code
        }));

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Éditeur de page</h1>

            <HorizontalTab
                tabItems={localesTab}
                onChange={onLocaleChange}
            />

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
                <DynamicForm
                    fields={fields}
                    values={content}
                    onChange={handleChange}
                />
            </div>

            <button
                onClick={handleSave}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
                Enregistrer
            </button>
        </div>
    );
}